import React, { useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { RouteHelpers, Routes } from "../../../../root/routes";
import { usePrimaryOrganization } from "../../../../hooks";
import {
  useRoPADetails,
  useRoPADownloader,
  useRoPAFunctions,
} from "../../../../../common/hooks/useRoPA";
import { StickyHeaderContainer } from "../../../../components/layout/header/StickyHeaderContainer";
import { Breadcrumbs, Button } from "@truevault/aquilo";
import { EyeIcon } from "@heroicons/react/24/solid";
import * as MuiIcons from "@mui/icons-material";
import { Done as DoneIcon, Visibility } from "@mui/icons-material";
import { Callout, CalloutVariant } from "../../../../components/Callout";
import { Chip, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { TechnicalAndOrganizationalSecurityMeasuresDialog } from "./dialogs/TechnicalAndOrganizationalSecurityMeasuresDialog";
import { Form } from "react-final-form";
import { WYSIWYGEditorInput } from "../../../../../common/components/input/WYSIWYGEditor";
import { AfterRoPASaveDialog } from "./dialogs/AfterRoPASaveDialog";
import { useHistory } from "react-router";
import { useSnackBar } from "@truevault/aquilo";

type PreviewSaveButtonsProps = {
  isSaving: boolean;
  saveButtonText: string;
  onClickPreview: () => void;
  onClickSave: () => void;
  isDisabledPreview: boolean;
  isDisabledSave: boolean;
};

const PreviewSaveButtons = ({
  isSaving,
  saveButtonText,
  onClickPreview,
  onClickSave,
  isDisabledPreview,
  isDisabledSave,
}: PreviewSaveButtonsProps) => {
  return (
    <div className="tw-boundary">
      <div className="flex justify-end align-items-center" style={{ gap: "0.5rem" }}>
        <small className="mr-sm">
          {isSaving ? (
            "Saving..."
          ) : (
            <div className="flex align-items-center">
              <DoneIcon className="mr-xxs" style={{ width: "18px" }} />
              <span>Saved</span>
            </div>
          )}
        </small>
        <Button color="secondary" onClick={onClickPreview} disabled={isDisabledPreview || isSaving}>
          <span className="flex items-center">
            <div style={{ marginRight: "8px" }}>
              <EyeIcon className="h-6 w-6" />
            </div>
            Preview
          </span>
        </Button>
        <Button color="primary" onClick={onClickSave} disabled={isDisabledSave || isSaving}>
          <span className="flex items-center">
            <div style={{ marginRight: "8px" }}>
              <MuiIcons.Save className="h-6 w-6" />
            </div>
            {saveButtonText}
          </span>
        </Button>
      </div>
    </div>
  );
};

export const RoPAPageEdit = () => {
  const [organization, organizationRequest] = usePrimaryOrganization();
  const orgId = organization?.id;
  const isUSOnly = !(organization?.featureGdpr || organization?.featurePipeda);
  const isGDPR = organization?.featureGdpr;

  const [ropaDetails, ropaDetailsRequest, invalidateRopaDetails] = useRoPADetails(orgId);
  const [isSecurityMeasuresExampleDialogOpen, setIsSecurityMeasuresExampleDialogOpen] =
    useState<boolean>(false);
  const [isAfterSaveDialogOpen, setIsAfterSaveDialogOpen] = useState<boolean>(false);

  const ropaDownloader = useRoPADownloader(orgId);
  const ropaFunctions = useRoPAFunctions(orgId);

  const history = useHistory();
  const snackbar = useSnackBar();
  const [isSaving, setIsSaving] = useState(false);

  const dataMapLink = (
    <div className="tw-boundary mt-sm" style={{ textAlign: "right" }}>
      <Button
        color={"secondary"}
        onClick={() => {
          history.push(Routes.dataMap.vendorDocumentation);
        }}
      >
        Go to Data Map
      </Button>
    </div>
  );

  return (
    <StandardPageWrapper requests={[organizationRequest, ropaDetailsRequest]}>
      <StickyHeaderContainer>
        <div className="tw-boundary">
          <Breadcrumbs
            title={["Record of Processing Activities (RoPA)", "Edit RoPA"]}
            source="Privacy Docs"
            sourceRoute={Routes.documents.root}
            contentInlineWithTitle={
              <PreviewSaveButtons
                isSaving={isSaving}
                saveButtonText={"Save Version"}
                onClickPreview={async () => {
                  setIsSaving(true);
                  await ropaDownloader.openRoPA(ropaDetails?.id);
                  setIsSaving(false);
                }}
                onClickSave={() => {
                  setIsSaving(true);
                  if (!ropaFunctions.publishRoPA()) {
                    snackbar("Couldn't publish RoPA - try again soon.", {
                      variant: "error",
                    });
                  } else {
                    setIsAfterSaveDialogOpen(true);
                    void invalidateRopaDetails();
                  }
                  setIsSaving(false);
                }}
                isDisabledPreview={!ropaDetails?.id}
                isDisabledSave={false}
              />
            }
          ></Breadcrumbs>
        </div>
      </StickyHeaderContainer>

      <h4 className="mt-md">How to update your RoPA</h4>
      <p>
        We have already created a document for you based on your business data profile. You can
        revise and update this document at any time and save a version for your records.
      </p>

      <Callout
        variant={CalloutVariant.LightPurpleBordered}
        title="1. Controller Details"
        subtitle="Add the name and contact information for your business."
        collapsed={false}
        collapsible={false}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Controller Name</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Controller Contact</TableCell>
              <TableCell>Location of Data Processing</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {ropaDetails?.controllers?.map((r) => (
              <TableRow key={r.name}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.website}</TableCell>
                <TableCell>{r.contact}</TableCell>
                <TableCell>{r.location}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="tw-boundary mt-sm" style={{ textAlign: "right" }}>
          <Button
            color={"secondary"}
            onClick={() => {
              history.push(RouteHelpers.organization.settings(orgId));
            }}
          >
            Go to Business Settings
          </Button>
        </div>
      </Callout>

      {!isUSOnly && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          title="2. Joint or Co-Controllers"
          subtitle="List data recipients that are classified as co-controllers with your business."
          collapsed={false}
          collapsible={false}
          className="mt-md"
        >
          {ropaDetails?.coControllers?.length ? (
            <Table sx={{ tableLayout: "fixed !important" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "150px !important" }}>Controller Name</TableCell>
                  <TableCell sx={{ width: "300px !important" }}>Website</TableCell>
                  <TableCell sx={{ width: "300px !important" }}>Controller Contact</TableCell>
                  <TableCell sx={{ width: "110px !important" }}>
                    Location of Data Processing
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {ropaDetails?.coControllers?.map((r) => (
                  <TableRow key={r.name}>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem !important",
                        lineHeight: "0.8rem !important",
                        verticalAlign: "top !important",
                      }}
                    >
                      {r.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem !important",
                        lineHeight: "0.8rem !important",
                        width: "300px !important",
                        verticalAlign: "top !important",
                      }}
                    >
                      <div style={{ overflowX: "auto", overflowY: "clip", whiteSpace: "nowrap" }}>
                        {r.website}
                      </div>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem !important",
                        lineHeight: "0.8rem !important",
                        width: "300px !important",
                        verticalAlign: "top !important",
                      }}
                    >
                      <div style={{ overflowX: "auto", overflowY: "clip", whiteSpace: "nowrap" }}>
                        {r.contact}
                      </div>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem !important",
                        lineHeight: "0.8rem !important",
                        verticalAlign: "top !important",
                      }}
                    >
                      {r.location.split(", ").join("\n")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p style={{ textAlign: "center" }}>
              <small>There are no joint or co-controllers.</small>
            </p>
          )}
          {dataMapLink}
        </Callout>
      )}

      <Callout
        variant={CalloutVariant.LightPurpleBordered}
        title={(isUSOnly ? "2. " : "3. ") + "Data Subjects"}
        subtitle="List each type or group of consumers whose data your business collects as a controller."
        collapsed={false}
        collapsible={false}
        className="mt-md"
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f3f0fe" }}>
              <TableCell sx={{ paddingTop: "0.5rem !important" }}>Collection Group Name</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {ropaDetails?.dataCollectedFromGroups?.map((r) => (
              <TableRow key={r.name}>
                <TableCell>{r.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {dataMapLink}
      </Callout>

      <Callout
        variant={CalloutVariant.LightPurpleBordered}
        title={(isUSOnly ? "3. " : "4. ") + "Processing Activities, Lawful Bases"}
        subtitle="List the data categories you collect, how you use the data, and under which lawful bases you collect it."
        collapsed={false}
        collapsible={false}
        className="mt-md"
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f4f9" }}>
              <TableCell sx={{ paddingTop: "0.5rem !important" }}>Processing Purposes</TableCell>
              <TableCell sx={{ paddingTop: "0.5rem !important" }}>Data Categories</TableCell>
              <TableCell sx={{ paddingTop: "0.5rem !important" }}>Lawful Bases</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {ropaDetails?.processingActivitiesAndLawfulBases?.map((r) => (
              <TableRow key={r.processingPurpose}>
                <TableCell sx={{ width: "200px !important" }}>{r.processingPurpose}</TableCell>
                <TableCell>{r.dataCategories?.join(", ")}</TableCell>
                <TableCell sx={{ width: "275px !important" }}>
                  {r.lawfulBases?.join(", ")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {dataMapLink}
      </Callout>

      {isGDPR && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          title={
            <div className="flex-row align-items-center justify-space-between mb-xs">
              <span>5. Data Recipients, Safe Transfer Mechanisms</span>
              {ropaDetails?.isMissingInfoSafeTransferMechanisms && (
                <Chip label={"Missing Info"} color="warning" variant="filled" />
              )}
            </div>
          }
          subtitle="List all data recipients as well as the transfer mechanisms relied on to safely transfer personal data outside of GDPR safe regions."
          collapsed={false}
          collapsible={false}
          className="mt-md"
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data Recipient</TableCell>
                <TableCell>Safe Transfer Mechanism</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {ropaDetails?.dataRecipientsAndSafeTransferMechanisms?.map((r) => (
                <TableRow key={r.dataRecipient}>
                  <TableCell>{r.dataRecipient}</TableCell>
                  <TableCell>
                    {r.safeTransferMechanismUrl ? (
                      <a href={r.safeTransferMechanismUrl} target="_blank" rel="noreferrer">
                        {r.safeTransferMechanismName}
                      </a>
                    ) : r.safeTransferMechanismName ? (
                      r.safeTransferMechanismName
                    ) : (
                      <span style={{ color: "#bd192c" /* (this is $color-red-600) */ }}>
                        Missing
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {dataMapLink}
        </Callout>
      )}

      <Callout
        variant={CalloutVariant.LightPurpleBordered}
        title={(isUSOnly ? "4. " : isGDPR ? "6. " : "5. ") + "Data Retention Schedule"}
        subtitle="For each processing activity, identify how long you intend to keep the related personal data."
        collapsed={false}
        collapsible={false}
        className="mt-md"
      >
        <>
          {ropaDetails?.dataRetentionPolicyCustomMarkup ? (
            <div
              dangerouslySetInnerHTML={ropaDetails?.dataRetentionPolicyCustomMarkup as any}
            ></div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type of Data</TableCell>
                  <TableCell>Retention Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ropaDetails?.dataRetentionPolicyDetails?.map((r) => (
                  <TableRow key={r.label}>
                    <TableCell style={{ verticalAlign: "top" }}>{r.label}</TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>{r.userText}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="tw-boundary mt-sm" style={{ textAlign: "right" }}>
            <Button
              color={"secondary"}
              onClick={() => {
                history.push(Routes.privacyCenter.dataRetention);
              }}
            >
              Edit Data Retention
            </Button>
          </div>
        </>
      </Callout>

      <Callout
        variant={CalloutVariant.LightPurpleBordered}
        title={
          <div className="flex-row align-items-center justify-space-between mb-xs">
            <span>
              {(isUSOnly ? "5. " : isGDPR ? "7. " : "6. ") +
                "Technical and Organizational Security Measures"}
            </span>
            {ropaDetails?.isMissingInfoTechnicalAndOrganizationalSecurityMeasures && (
              <Chip label={"Missing Info"} color="warning" variant="filled" />
            )}
          </div>
        }
        subtitle="Describe the measures you have implemented to secure personal data."
        collapsed={false}
        collapsible={false}
        className="mt-md"
      >
        <Form
          initialValues={{ answer: ropaDetails?.technicalAndOrganizationalSecurityMeasuresMarkup }}
          onSubmit={async (values) => {
            setIsSaving(true);
            await ropaFunctions.setMeasures(values.answer);
            setIsSaving(false);
            setTimeout(() => {
              invalidateRopaDetails();
            }, 250);
          }}
          render={({ handleSubmit }) => (
            <form>
              <WYSIWYGEditorInput
                field={`answer`}
                height="200px"
                onBlur={handleSubmit}
              ></WYSIWYGEditorInput>
            </form>
          )}
        />
        <div className="tw-boundary mt-sm flex-row justify-space-between">
          <Button
            color={"tertiary"}
            onClick={() => {
              setIsSecurityMeasuresExampleDialogOpen(true);
            }}
          >
            <Visibility
              className="mr-xs"
              sx={{ width: "15px", height: "15px", color: "#918CA1" }}
            />
            <span>See examples</span>
          </Button>
        </div>
      </Callout>

      <TechnicalAndOrganizationalSecurityMeasuresDialog
        isOpen={isSecurityMeasuresExampleDialogOpen}
        onClose={() => {
          setIsSecurityMeasuresExampleDialogOpen(false);
        }}
      />
      <AfterRoPASaveDialog
        isOpen={isAfterSaveDialogOpen}
        onClose={() => {
          setIsAfterSaveDialogOpen(false);
        }}
      />
    </StandardPageWrapper>
  );
};
